<template>
  <div class="report-manage-page">
    <!-- Header -->
    <div class="report-header d-flex align-center justify-space-between px-3 px-sm-6 py-3">
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn icon @click="$router.push({ name: 'report-catalog' })">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h2 class="mb-0 primary--text font-weight-bold" style="font-size: 20px;">
          <v-icon color="primary" class="mr-1" style="vertical-align: middle;">mdi-account-group</v-icon>
          Team Catalog Report
        </h2>
      </div>
      <span class="grey--text text--darken-1 d-none d-sm-inline" style="font-size: 13px;">Sellers managed by you</span>
    </div>

    <v-divider />

    <!-- Body -->
    <div class="report-body pa-3 pa-sm-5">
      <!-- ===== Date filter (global) ===== -->
      <v-card rounded="lg" class="pa-4 mb-4 date-filter-card" elevation="1">
        <div class="d-flex align-center justify-space-between" style="gap: 12px; flex-wrap: wrap;">
          <div class="d-flex align-center" style="gap: 8px;">
            <v-icon color="primary">mdi-calendar</v-icon>
            <span class="font-weight-bold primary--text" style="font-size: 15px;">Report Date</span>
          </div>
          <div class="d-flex align-center" style="gap: 8px; flex-wrap: wrap;">
            <v-btn small outlined color="primary" style="text-transform: none;" @click="goPrevRange">
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  outlined
                  color="primary"
                  style="min-width: 260px; text-transform: none;"
                >
                  <v-icon small left>mdi-calendar-range</v-icon>
                  {{ formatRangeDisplay(timeStart, timeEnd) }}
                </v-btn>
              </template>
              <v-date-picker v-model="dateRange" no-title scrollable range :max="today" @input="onDateRangeInput" />
            </v-menu>
            <v-btn
              small
              outlined
              color="primary"
              style="text-transform: none;"
              :disabled="timeEnd >= today"
              @click="goNextRange"
            >
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn small text color="primary" style="text-transform: none;" @click="goToday">
              <v-icon small left>mdi-calendar-today-outline</v-icon>
              Today
            </v-btn>
            <v-btn depressed color="primary" :loading="isLoading" @click="fetchData">
              <v-icon small left>mdi-refresh</v-icon>
              Apply
            </v-btn>
          </div>
        </div>
      </v-card>

      <v-row no-gutters style="gap: 0; align-items: flex-start;">
        <!-- ===== LEFT: Seller list ===== -->
        <v-col cols="12" lg="4" class="pr-lg-4 mb-4 mb-lg-0">
          <v-card rounded="lg" class="pa-3 seller-list-card" elevation="1">
            <div class="d-flex align-center justify-space-between mb-3 px-1" style="gap: 8px;">
              <h3 class="primary--text font-weight-bold mb-0 d-flex align-center" style="font-size: 15px;">
                <v-icon color="primary" small class="mr-1">mdi-account-multiple-outline</v-icon>
                Sellers
                <v-chip v-if="sellers.length" x-small label color="primary" outlined class="ml-2 font-weight-bold">
                  {{ sellers.length }}
                </v-chip>
              </h3>
              <v-btn icon small color="primary" :loading="isLoading" @click="fetchData">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>

            <v-text-field
              v-model="search"
              placeholder="Search seller by name / email…"
              prepend-inner-icon="mdi-magnify"
              hide-details
              outlined
              dense
              clearable
              class="mb-3"
            />

            <div class="seller-list">
              <div v-if="isLoading && !sellers.length && !teamSummary" class="py-8 text-center grey--text">
                <v-progress-circular indeterminate color="primary" size="30" />
                <div class="mt-2" style="font-size: 13px;">Loading sellers…</div>
              </div>

              <!-- Team overview pinned item -->
              <div
                v-if="teamSummary"
                class="seller-item team-overview-item"
                :class="{ 'seller-item--active': isTeamView }"
                @click="selectTeam"
              >
                <div class="seller-item__avatar team-overview-item__avatar">
                  <v-icon color="white" size="20">mdi-account-group</v-icon>
                </div>
                <div class="seller-item__body">
                  <div class="seller-item__name">Team Overview</div>
                  <div class="seller-item__email">
                    <v-icon size="12" color="grey">mdi-account-multiple-outline</v-icon>
                    All {{ sellers.length }} seller{{ sellers.length === 1 ? '' : 's' }}
                  </div>
                  <div class="seller-item__stats">
                    <span class="seller-stat seller-stat--spent">
                      Spent {{ formatCurrency(teamSummary.summary && teamSummary.summary.totalSpent) }}
                    </span>
                    <span class="seller-stat" :class="roasChipClass(teamSummary.summary && teamSummary.summary.roas)">
                      ROAS {{ Number((teamSummary.summary && teamSummary.summary.roas) || 0).toFixed(2) }}x
                    </span>
                    <span
                      class="seller-stat"
                      :class="profitChipClass(teamSummary.summary && teamSummary.summary.profitSeller)"
                    >
                      Profit {{ formatCurrency(teamSummary.summary && teamSummary.summary.profitSeller) }}
                    </span>
                  </div>
                </div>
                <v-icon v-if="isTeamView" color="primary" size="20" class="seller-item__check"
                  >mdi-chevron-right</v-icon
                >
              </div>

              <v-divider v-if="teamSummary && filteredSellers.length" class="my-2" />

              <div v-if="!isLoading && !filteredSellers.length && !teamSummary" class="py-8 text-center grey--text">
                <v-icon large color="grey lighten-2">mdi-account-off-outline</v-icon>
                <div class="mt-2" style="font-size: 13px;">No sellers found</div>
              </div>

              <div
                v-for="entry in filteredSellers"
                :key="entry.seller._id"
                class="seller-item"
                :class="{ 'seller-item--active': isActive(entry) }"
                @click="selectSeller(entry)"
              >
                <div class="seller-item__avatar" :class="{ 'seller-item__avatar--active': isActive(entry) }">
                  {{ sellerInitials(entry.seller) }}
                </div>
                <div class="seller-item__body">
                  <div class="seller-item__name">{{ sellerName(entry.seller) }}</div>
                  <div class="seller-item__email">
                    <v-icon size="12" color="grey">mdi-email-outline</v-icon>
                    {{ entry.seller.email || '—' }}
                  </div>
                  <div class="seller-item__stats">
                    <span class="seller-stat seller-stat--spent">
                      Spent {{ formatCurrency(entry.summary && entry.summary.totalSpent) }}
                    </span>
                    <span class="seller-stat" :class="roasChipClass(entry.summary && entry.summary.roas)">
                      ROAS {{ Number((entry.summary && entry.summary.roas) || 0).toFixed(2) }}x
                    </span>
                    <span class="seller-stat" :class="profitChipClass(entry.summary && entry.summary.profitSeller)">
                      Profit {{ formatCurrency(entry.summary && entry.summary.profitSeller) }}
                    </span>
                  </div>
                </div>
                <v-icon v-if="isActive(entry)" color="primary" size="20" class="seller-item__check"
                  >mdi-chevron-right</v-icon
                >
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- ===== RIGHT: Selected seller report (read-only) ===== -->
        <v-col cols="12" lg="8">
          <!-- Empty / no selection -->
          <v-card
            v-if="!selectedSeller && !isTeamView"
            rounded="lg"
            class="pa-10 text-center detail-empty"
            elevation="1"
          >
            <v-icon size="64" color="grey lighten-2">mdi-chart-box-outline</v-icon>
            <div class="mt-3 grey--text" style="font-size: 15px;">
              {{ sellers.length ? 'Select a seller or view team overview' : 'No data for this range' }}
            </div>
          </v-card>

          <template v-else>
            <!-- Team header -->
            <v-card v-if="isTeamView" rounded="lg" class="pa-4 mb-4 seller-detail-head" elevation="1">
              <div class="d-flex align-center" style="gap: 14px;">
                <div class="seller-detail-head__avatar team-overview-item__avatar">
                  <v-icon color="white" size="24">mdi-account-group</v-icon>
                </div>
                <div style="min-width: 0;">
                  <div class="seller-detail-head__name">Team Overview</div>
                  <div class="seller-detail-head__email">
                    <v-icon size="14" color="grey">mdi-account-multiple-outline</v-icon>
                    {{ sellers.length }} seller{{ sellers.length === 1 ? '' : 's' }} combined
                  </div>
                </div>
                <v-spacer />
                <v-chip small label color="primary" outlined class="font-weight-bold d-none d-sm-flex">
                  <v-icon x-small left>mdi-account-group</v-icon>
                  Team
                </v-chip>
              </div>
            </v-card>

            <!-- Seller header -->
            <v-card v-else rounded="lg" class="pa-4 mb-4 seller-detail-head" elevation="1">
              <div class="d-flex align-center" style="gap: 14px;">
                <div class="seller-detail-head__avatar">{{ sellerInitials(selectedSeller.seller) }}</div>
                <div style="min-width: 0;">
                  <div class="seller-detail-head__name">{{ sellerName(selectedSeller.seller) }}</div>
                  <div class="seller-detail-head__email">
                    <v-icon size="14" color="grey">mdi-email-outline</v-icon>
                    {{ selectedSeller.seller.email || '—' }}
                  </div>
                </div>
                <v-spacer />
                <v-chip small label color="primary" outlined class="font-weight-bold d-none d-sm-flex">
                  <v-icon x-small left>mdi-eye-outline</v-icon>
                  View only
                </v-chip>
              </div>
            </v-card>

            <!-- Summary (by selected date range) -->
            <v-card rounded="lg" class="pa-3 mb-4 summary-card summary-card--day" elevation="1">
              <div class="d-flex align-center mb-2" style="gap: 6px;">
                <div class="summary-card__icon summary-card__icon--day">
                  <v-icon size="16" color="white">mdi-chart-box-outline</v-icon>
                </div>
                <div>
                  <div class="summary-card__title">Summary</div>
                  <div class="summary-card__subtitle">{{ formatRangeDisplay(timeStart, timeEnd) }}</div>
                </div>
              </div>
              <div class="summary-grid">
                <div class="summary-cell summary-cell--orders">
                  <span class="summary-cell__label">Orders</span>
                  <span class="summary-cell__value">{{ num(currentSummary.totalOrder) }}</span>
                </div>
                <div class="summary-cell summary-cell--spent">
                  <span class="summary-cell__label">Spent</span>
                  <span class="summary-cell__value">{{ formatCurrency(currentSummary.totalSpent) }}</span>
                </div>
                <div class="summary-cell summary-cell--rev">
                  <span class="summary-cell__label">Revenue</span>
                  <span class="summary-cell__value">{{ formatCurrency(currentSummary.totalRev) }}</span>
                </div>
                <div class="summary-cell" :class="roasChipClass(currentSummary.roas)">
                  <span class="summary-cell__label">ROAS</span>
                  <span class="summary-cell__value" :class="roasTextClass(currentSummary.roas)">
                    {{ Number(currentSummary.roas || 0).toFixed(2) }}x
                  </span>
                </div>
                <div class="summary-cell" :class="profitCellClass(currentSummary.profitSeller)">
                  <span class="summary-cell__label">Profit Seller</span>
                  <span class="summary-cell__value" :class="profitTextClass(currentSummary.profitSeller)">
                    {{ formatCurrency(currentSummary.profitSeller) }}
                  </span>
                </div>
              </div>
            </v-card>

            <!-- Daily catalog table (read-only) -->
            <v-card rounded="lg" class="pa-4 mb-4 catalog-card" elevation="1">
              <div class="d-flex align-center mb-4" style="gap: 8px;">
                <v-icon color="primary" small>mdi-calendar-range</v-icon>
                <h3 class="primary--text font-weight-bold mb-0" style="font-size: 15px;">Catalog</h3>
                <span class="catalog-card__period">{{ formatRangeDisplay(timeStart, timeEnd) }}</span>
                <v-chip v-if="currentList.length" x-small label color="primary" outlined class="ml-1 font-weight-bold">
                  {{ currentList.length }}
                </v-chip>
              </div>
              <div class="table-scroll-wrapper">
                <v-data-table
                  :headers="dailyHeaders"
                  :items="currentList"
                  :items-per-page="-1"
                  hide-default-footer
                  item-key="catalog"
                  class="report-table"
                >
                  <template v-slot:[`item.image`]="{ item }">
                    <div class="catalog-thumb-wrap">
                      <v-img
                        v-if="getCatalogImage(item)"
                        :src="imageHelpers.url(getCatalogImage(item), { width: 80, height: 80 })"
                        width="48"
                        height="48"
                        class="rounded catalog-thumb"
                        contain
                      />
                      <div v-else class="catalog-thumb-placeholder">
                        <v-icon color="grey lighten-1">mdi-image-off-outline</v-icon>
                      </div>
                    </div>
                  </template>
                  <template v-slot:[`item.name`]="{ item }">
                    <v-tooltip top max-width="320">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="cell-clamp font-weight-medium primary--text">
                          {{ getCatalogName(item) }}
                        </div>
                      </template>
                      <span>{{ getCatalogName(item) }}</span>
                    </v-tooltip>
                  </template>
                  <template v-slot:[`item.totalOrder`]="{ item }">
                    <div class="order-cell">{{ num(item.totalOrder) }}</div>
                  </template>
                  <template v-slot:[`item.totalSpent`]="{ item }">
                    <span class="red--text text--darken-1 font-weight-medium">{{
                      formatCurrency(item.totalSpent)
                    }}</span>
                  </template>
                  <template v-slot:[`item.totalRev`]="{ item }">
                    <span class="green--text text--darken-2 font-weight-medium">{{
                      formatCurrency(item.totalRev)
                    }}</span>
                  </template>
                  <template v-slot:[`item.roas`]="{ item }">
                    <span class="font-weight-medium" :class="roasTextClass(item.roas)">
                      {{ Number(item.roas || 0).toFixed(2) }}x
                    </span>
                  </template>
                  <template v-slot:[`item.note`]="{ item }">
                    <div v-if="item.note" class="note-readonly">{{ item.note }}</div>
                    <span v-else class="note-readonly__empty">—</span>
                  </template>
                  <template v-slot:no-data>
                    <div class="text-center py-8 grey--text">
                      <v-icon large color="grey lighten-2">mdi-package-variant-closed-remove</v-icon>
                      <div class="mt-2" style="font-size: 13px;">No catalog data on this day</div>
                    </div>
                  </template>
                </v-data-table>
              </div>
            </v-card>
          </template>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { reportApi } from '@/apis/report';
import imageHelpers from '@/helpers/image';

export default {
  name: 'ReportCatalogManagePage',
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      today,
      imageHelpers,
      timeStart: today,
      timeEnd: today,
      dateRange: [today, today],
      dateMenu: false,
      isLoading: false,
      sellers: [],
      teamSummary: null,
      selectedSellerId: null,
      search: '',
      dailyHeaders: [
        { text: 'Image', value: 'image', sortable: false, width: '80px' },
        { text: 'Catalog Name', value: 'name', sortable: false },
        { text: 'Orders', value: 'totalOrder', sortable: true, width: '100px', align: 'center' },
        { text: 'Spent', value: 'totalSpent', sortable: true, width: '120px', align: 'end' },
        { text: 'Revenue', value: 'totalRev', sortable: true, width: '120px', align: 'end' },
        { text: 'ROAS', value: 'roas', sortable: true, width: '90px', align: 'end' },
        { text: 'Note', value: 'note', sortable: false, width: '260px' },
      ],
    };
  },
  computed: {
    filteredSellers() {
      const q = (this.search || '').trim().toLowerCase();
      if (!q) return this.sellers;
      return this.sellers.filter(e => {
        const s = e.seller || {};
        return (
          (s.email || '').toLowerCase().includes(q) ||
          (s.firstName || '').toLowerCase().includes(q) ||
          (s.lastName || '').toLowerCase().includes(q)
        );
      });
    },
    selectedSeller() {
      if (!this.selectedSellerId) return null;
      return this.sellers.find(e => e.seller && e.seller._id === this.selectedSellerId) || null;
    },
    isTeamView() {
      return !this.selectedSellerId && !!this.teamSummary;
    },
    currentList() {
      if (this.isTeamView) {
        return Array.isArray(this.teamSummary.data) ? this.teamSummary.data : [];
      }
      return (this.selectedSeller && Array.isArray(this.selectedSeller.data) && this.selectedSeller.data) || [];
    },
    currentSummary() {
      if (this.isTeamView) return this.teamSummary.summary || {};
      return (this.selectedSeller && this.selectedSeller.summary) || {};
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    /* ---------- Formatting helpers ---------- */
    num(val) {
      return Number(val || 0).toLocaleString();
    },
    formatCurrency(val) {
      if (val === null || val === undefined || val === '') return '$0.00';
      const n = parseFloat(val);
      if (isNaN(n)) return '$0.00';
      if (n < 0) {
        return '-$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDisplayDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    },
    formatRangeDisplay(start, end) {
      if (!start && !end) return '';
      if (!end || start === end) return this.formatDisplayDate(start);
      const fmtShort = s => {
        const d = new Date(s + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      };
      return `${fmtShort(start)} → ${fmtShort(end)}`;
    },
    monthLabel(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    },
    toDateString(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    rangeDays() {
      const s = new Date(this.timeStart + 'T00:00:00');
      const e = new Date(this.timeEnd + 'T00:00:00');
      const diff = Math.round((e - s) / 86400000);
      return Math.max(0, diff) + 1;
    },
    /* ---------- Date nav ---------- */
    onDateRangeInput(val) {
      if (!Array.isArray(val) || val.length < 2) return;
      const sorted = [...val].sort();
      this.timeStart = sorted[0];
      this.timeEnd = sorted[1];
      this.dateRange = sorted;
      this.dateMenu = false;
      this.fetchData();
    },
    shiftRange(direction) {
      const days = this.rangeDays();
      const start = new Date(this.timeStart + 'T00:00:00');
      const end = new Date(this.timeEnd + 'T00:00:00');
      start.setDate(start.getDate() + direction * days);
      end.setDate(end.getDate() + direction * days);
      let nextStart = this.toDateString(start);
      let nextEnd = this.toDateString(end);
      if (nextEnd > this.today) {
        const overflow = Math.round((new Date(nextEnd + 'T00:00:00') - new Date(this.today + 'T00:00:00')) / 86400000);
        const adjStart = new Date(nextStart + 'T00:00:00');
        adjStart.setDate(adjStart.getDate() - overflow);
        nextStart = this.toDateString(adjStart);
        nextEnd = this.today;
      }
      this.timeStart = nextStart;
      this.timeEnd = nextEnd;
      this.dateRange = [nextStart, nextEnd];
      this.fetchData();
    },
    goPrevRange() {
      this.shiftRange(-1);
    },
    goNextRange() {
      if (this.timeEnd >= this.today) return;
      this.shiftRange(1);
    },
    goToday() {
      if (this.timeStart === this.today && this.timeEnd === this.today) return;
      this.timeStart = this.today;
      this.timeEnd = this.today;
      this.dateRange = [this.today, this.today];
      this.fetchData();
    },
    /* ---------- Catalog helpers ---------- */
    getCatalogImage(item) {
      if (!item) return '';
      const detail = item.catalogDetail || {};
      return (
        (detail.images && detail.images[0]) ||
        detail.image ||
        detail.thumbnail ||
        item.image ||
        (item.images && item.images[0]) ||
        ''
      );
    },
    getCatalogName(item) {
      if (!item) return '—';
      const detail = item.catalogDetail || {};
      return detail.title || detail.name || item.title || item.name || '—';
    },
    roasTextClass(roas) {
      const r = Number(roas) || 0;
      if (r >= 2) return 'green--text text--darken-2';
      if (r >= 1) return 'orange--text text--darken-2';
      return 'red--text text--darken-1';
    },
    roasChipClass(roas) {
      const r = Number(roas) || 0;
      if (r >= 2) return 'totals-chip--roas-good';
      if (r >= 1) return 'totals-chip--roas-ok';
      return 'totals-chip--roas-bad';
    },
    profitTextClass(val) {
      const n = parseFloat(val) || 0;
      return n < 0 ? 'red--text text--darken-1' : 'green--text text--darken-2';
    },
    profitChipClass(val) {
      const n = parseFloat(val) || 0;
      return n < 0 ? 'seller-stat--profit-loss' : 'seller-stat--profit';
    },
    profitCellClass(val) {
      const n = parseFloat(val) || 0;
      return n < 0 ? 'summary-cell--profit-loss' : 'summary-cell--profit';
    },
    /* ---------- Seller helpers ---------- */
    sellerName(seller) {
      if (!seller) return '—';
      const full = [seller.firstName, seller.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
      return full || seller.email || '—';
    },
    sellerInitials(seller) {
      if (!seller) return '?';
      const a = (seller.firstName || '').trim();
      const b = (seller.lastName || '').trim();
      const init = `${a.charAt(0)}${b.charAt(0)}`.trim();
      return (init || (seller.email || '?').charAt(0)).toUpperCase();
    },
    isActive(entry) {
      return entry.seller && entry.seller._id === this.selectedSellerId;
    },
    selectSeller(entry) {
      if (entry && entry.seller) this.selectedSellerId = entry.seller._id;
    },
    selectTeam() {
      this.selectedSellerId = null;
    },
    /* ---------- Fetch ---------- */
    async fetchData() {
      this.isLoading = true;
      try {
        const { data } = await reportApi.getReportCatalogByManageSeller({
          timeStart: this.timeStart,
          timeEnd: this.timeEnd,
        });
        const body = data && !Array.isArray(data) ? data : null;
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        this.sellers = list;
        this.teamSummary = (body && body.teamSummary) || null;
        // Keep current selection if still present; otherwise default to team overview
        const stillExists = this.selectedSellerId && list.some(e => e.seller && e.seller._id === this.selectedSellerId);
        if (!stillExists) {
          this.selectedSellerId = null;
        }
      } catch (e) {
        console.error('fetchManageSeller', e);
        this.sellers = [];
        this.teamSummary = null;
        this.selectedSellerId = null;
        this.$store.commit('setMessages', {
          messages: e.response?.data?.message || 'Failed to load team report',
          type: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.report-manage-page {
  min-height: 100vh;
  background: #f4f6fb;
  display: flex;
  flex-direction: column;
}

.report-header {
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 10;
}

.report-body {
  flex: 1;
  align-items: flex-start;
}

.date-filter-card {
  background: linear-gradient(90deg, #f0f4ff 0%, #ffffff 100%);
  border: 1px solid #e3e7f5;
}

/* ===== Seller list ===== */
.seller-list-card {
  border: 1px solid #e3e7f5;
  position: sticky;
  top: 84px;
}

.seller-list {
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 2px;
}

.seller-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #eef0fb;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;

  &:hover {
    background: #f8f9ff;
    border-color: #c5cae9;
  }

  &--active {
    background: #eef1ff;
    border-color: #3f51b5;
    box-shadow: 0 0 0 2px rgba(63, 81, 181, 0.12);
  }

  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
    color: #fff;
    background: linear-gradient(135deg, #7986cb 0%, #9575cd 100%);

    &--active {
      background: linear-gradient(135deg, #3f51b5 0%, #7c4dff 100%);
    }
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-weight: 700;
    font-size: 13.5px;
    color: #2c3e7a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__email {
    font-size: 11.5px;
    color: #888;
    display: flex;
    align-items: center;
    gap: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__stats {
    display: flex;
    gap: 6px;
    margin-top: 5px;
    flex-wrap: wrap;
  }

  &__check {
    flex-shrink: 0;
  }
}

.team-overview-item__avatar {
  background: linear-gradient(135deg, #00897b 0%, #26a69a 100%) !important;
}

.seller-stat {
  font-size: 10.5px;
  font-weight: 600;
  border-radius: 6px;
  padding: 2px 6px;
  background: #f0f2f8;
  color: #555;
  border: 1px solid #e6e9f5;

  &--spent {
    background: #fef2f2;
    color: #c62828;
    border-color: #ffd2d2;
  }

  &--profit {
    background: #f1f8f1;
    color: #2e7d32;
    border-color: #c8e6c9;
  }
  &--profit-loss {
    background: #fef2f2;
    color: #c62828;
    border-color: #ffd2d2;
  }

  &.totals-chip--roas-good {
    background: #f1f8f1;
    color: #2e7d32;
    border-color: #c8e6c9;
  }
  &.totals-chip--roas-ok {
    background: #fff8e1;
    color: #f57f17;
    border-color: #ffe0a3;
  }
  &.totals-chip--roas-bad {
    background: #fef2f2;
    color: #c62828;
    border-color: #ffd2d2;
  }
}

/* ===== Detail ===== */
.detail-empty {
  border: 1px dashed #d6d9ee;
}

.seller-detail-head {
  border: 1px solid #e3e7f5;
  background: linear-gradient(135deg, #eef1ff 0%, #faf8ff 100%);

  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 18px;
    color: #fff;
    background: linear-gradient(135deg, #3f51b5 0%, #7c4dff 100%);
    box-shadow: 0 3px 8px rgba(63, 81, 181, 0.25);
  }

  &__name {
    font-size: 17px;
    font-weight: 700;
    color: #2c3e7a;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__email {
    font-size: 12.5px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
    word-break: break-all;
  }
}

/* ===== Summary cards ===== */
.summary-card {
  border: 1px solid #e3e7f5;
  height: 100%;

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    &--day {
      background: linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%);
    }
    &--month {
      background: linear-gradient(135deg, #7c4dff 0%, #b388ff 100%);
    }
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #2c3e7a;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 11px;
    color: #888;
  }

  &--day {
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  }
  &--month {
    background: linear-gradient(135deg, #f3edff 0%, #fbf8ff 100%);
    border-color: #d9ccff;
  }
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}

.summary-cell {
  background: #fff;
  border-radius: 8px;
  padding: 6px 8px;
  border: 1px solid #eef0fb;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  &__label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #888;
    font-weight: 600;
  }
  &__value {
    font-size: 13px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &--rev {
    background: #f1f8f1;
    border-color: #c8e6c9;
    .summary-cell__value {
      color: #2e7d32;
    }
  }
  &--profit {
    background: #f1f8f1;
    border-color: #c8e6c9;
    .summary-cell__value {
      color: #2e7d32;
    }
  }
  &--profit-loss {
    background: #fef2f2;
    border-color: #ffd2d2;
    .summary-cell__value {
      color: #c62828;
    }
  }
  &--spent {
    background: #fef2f2;
    border-color: #ffd2d2;
    .summary-cell__value {
      color: #c62828;
    }
  }
  &--orders {
    background: #e3f2fd;
    border-color: #bbdefb;
    .summary-cell__value {
      color: #1565c0;
    }
  }
}

.summary-cell.totals-chip--roas-good {
  background: #f1f8f1;
  border-color: #c8e6c9;
}
.summary-cell.totals-chip--roas-ok {
  background: #fff8e1;
  border-color: #ffe0a3;
}
.summary-cell.totals-chip--roas-bad {
  background: #fef2f2;
  border-color: #ffd2d2;
}

.summary-card--month .summary-cell {
  background: #fff;
  border-color: #e3d9ff;

  &--rev {
    background: #ecf7ee;
    border-color: #b8dcc0;
    .summary-cell__value {
      color: #1b5e20;
    }
  }
  &--profit {
    background: #ecf7ee;
    border-color: #b8dcc0;
    .summary-cell__value {
      color: #1b5e20;
    }
  }
  &--profit-loss {
    background: #fdecec;
    border-color: #f3bfbf;
    .summary-cell__value {
      color: #8e1f1f;
    }
  }
  &--spent {
    background: #fdecec;
    border-color: #f3bfbf;
    .summary-cell__value {
      color: #8e1f1f;
    }
  }
  &--orders {
    background: #ede7ff;
    border-color: #cbb9ff;
    .summary-cell__value {
      color: #4527a0;
    }
  }
  &.totals-chip--roas-good {
    background: #ecf7ee;
    border-color: #b8dcc0;
  }
  &.totals-chip--roas-ok {
    background: #fff3d6;
    border-color: #f3d28a;
  }
  &.totals-chip--roas-bad {
    background: #fdecec;
    border-color: #f3bfbf;
  }
}

@media (max-width: 960px) {
  .summary-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 599px) {
  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ===== Tables ===== */
.catalog-card {
  border: 1px solid #e3e7f5;
}
.catalog-card--month {
  border-color: #e3d9ff;
  background: linear-gradient(135deg, #fbf9ff 0%, #ffffff 100%);
}

.catalog-card__period {
  font-size: 12px;
  font-weight: 600;
  color: #9094a8;
  white-space: nowrap;
}

.table-scroll-wrapper {
  overflow-x: auto;
  border-radius: 8px;
}

.report-table {
  border-radius: 8px;

  ::v-deep thead th {
    background: #f0f4ff !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #3f51b5 !important;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
    white-space: nowrap;
  }

  ::v-deep tbody td {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
    font-size: 14px !important;
    vertical-align: top;
  }

  ::v-deep tbody tr:hover {
    background: #f8f9ff !important;
  }
}

.report-table--month {
  ::v-deep thead th {
    background: #f3edff !important;
    color: #5e35b1 !important;
  }
  ::v-deep tbody tr:hover {
    background: #f7f3ff !important;
  }
}

.cell-clamp {
  max-width: 100%;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.order-cell {
  display: inline-block;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #1565c0;
  background: #e3f2fd;
  border-radius: 8px;
  padding: 4px 12px;
  min-width: 50px;
}

.catalog-thumb-wrap {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.catalog-thumb {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
}
.catalog-thumb-placeholder {
  width: 48px;
  height: 48px;
  border: 1px dashed #c5cae9;
  border-radius: 6px;
  background: #f5f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.note-readonly {
  font-size: 12.5px;
  line-height: 1.45;
  color: #2c3e7a;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f8f9ff;
  border: 1px solid #eef0fb;
  border-radius: 8px;
  padding: 6px 10px;
  max-height: 140px;
  overflow-y: auto;

  &__empty {
    color: #bbb;
  }
}

/* ===== Plan ===== */
.plan-card {
  border: 1px solid #e3e7f5;
  background: linear-gradient(135deg, #fdfcff 0%, #f6f8ff 100%);

  &__icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #3f51b5 0%, #7c4dff 100%);
    box-shadow: 0 4px 10px rgba(63, 81, 181, 0.25);
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: #2c3e7a;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 12px;
    color: #888;
  }
}

.plan-display {
  background: #ffffff;
  border: 1px solid #d6d9ee;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.55;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 70px;

  &__placeholder {
    color: #aaa;
    font-style: italic;
  }
}
</style>
